import { supabase } from '../supabaseClient';

/**
 * Shared master data (coverages, disciplines, specializations).
 *
 * Governance: any professional may ADD an entry, but only admin may remove one.
 * A professional who wants something gone files a removal request instead.
 * RLS enforces this; the UI only reflects it.
 */

export const ENTITY = {
  COMPANY: 'insurance_company',
  PLAN: 'insurance_plan',
  DISCIPLINE: 'discipline',
  SPECIALIZATION: 'specialization',
};

const TABLE_FOR = {
  [ENTITY.COMPANY]: 'insurance_companies',
  [ENTITY.PLAN]: 'insurance_plans',
  [ENTITY.DISCIPLINE]: 'disciplines',
  [ENTITY.SPECIALIZATION]: 'specializations',
};

/**
 * Counts what would break if an entry were removed.
 *
 * Master data is referenced by historical appointments and patient records, so
 * an entry that has ever been used must never be hard-deleted — that would
 * erase what a past session was billed under. Those get deactivated instead,
 * which removes them from every picker while leaving history readable.
 *
 * Counted in the database, not from here. Counting through admin's own policies
 * stopped seeing other professionals' patients once those became theirs alone
 * (migration 0038), and would have reported a coverage half the practice bills
 * under as unused — and deleted it.
 */
export const countUsage = async (entityType, id) => {
  const { data, error } = await supabase.rpc('master_data_usage', {
    p_entity_type: entityType,
    p_entity_id: id,
  });
  if (error) throw error;
  return data ?? 0;
};

/**
 * Admin removal. Hard-deletes only entries nothing references; anything in use
 * is deactivated so historical records keep their meaning.
 * Returns how it was removed, so the UI can say which happened.
 */
export const removeEntry = async (entityType, id) => {
  const table = TABLE_FOR[entityType];
  let usage;
  try {
    usage = await countUsage(entityType, id);
  } catch (error) {
    // Without a count nothing is deleted: guessing zero is how in-use entries
    // would disappear.
    return { error };
  }

  if (usage === 0) {
    const { error } = await supabase.from(table).delete().eq('id', id);
    if (error) return { error };
    return { mode: 'deleted', error: null };
  }

  const { error } = await supabase.from(table).update({ is_active: false }).eq('id', id);
  if (error) return { error };
  return { mode: 'deactivated', usage, error: null };
};

export const reactivateEntry = (entityType, id) =>
  supabase.from(TABLE_FOR[entityType]).update({ is_active: true }).eq('id', id);

/**
 * A professional asks admin to remove something they cannot remove themselves.
 * The database stores the entry's name with the request and files it pending,
 * whatever else is sent (0066).
 */
export const requestRemoval = ({ therapistId, entityType, entityId, reason }) =>
  supabase.from('master_data_removal_requests').insert([{
    requested_by_therapist_id: therapistId,
    entity_type: entityType,
    entity_id: entityId,
    reason: reason || null,
  }]);

/**
 * What uses an entry, by kind, for admin to read before answering a removal
 * request (0066): `{ usage, total, exists, subcategories }`. Counts only, never
 * names; `total` decides whether approving deletes the entry or deactivates it.
 */
export const usageDetail = async (entityType, id) => {
  const { data, error } = await supabase.rpc('master_data_usage_detail', {
    p_entity_type: entityType,
    p_entity_id: id,
  });
  if (error) throw error;
  return data;
};

/**
 * Approves or rejects a removal request in one transaction (0066). Approving
 * removes the entry by removeEntry's rule, and the request keeps who answered,
 * the entry's name, what happened to it and what used it, so the log still says
 * what was approved once the entry is gone. Resolves to
 * `{ status, label, outcome, usage, total }`.
 */
export const resolveRemovalRequest = ({ requestId, approve, notes }) =>
  supabase.rpc('resolve_removal_request', {
    p_request_id: requestId,
    p_approve: approve,
    p_notes: notes || null,
  });
